import { useState, useContext, useEffect } from "react";
import { ProductContext } from "../context/AppContext";
import Header from "./Header";
import { getCachedData, setCachedData } from "../utils/apiCache";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import Swal from "sweetalert2";
import { Container, Row, Col, Card, Button, Modal, Spinner, Badge, InputGroup, Form } from "react-bootstrap";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaPlus,
  FaSearch,
  FaFilter,
  FaSync,
  FaEye,
  FaFilePdf
} from "react-icons/fa";
import ImageWithBasePath from "../core/common/imageWithBasePath";
import moment from "moment";

function ApplyLeave() {
    const { userId } = useContext(ProductContext);

    const [formData, setFormData] = useState({
        startDate: "",
        endDate: "",
        leaveType: "",
        remarks: "",
        userId: userId,
    });

    useEffect(() => {
        if (userId) {
            setFormData((prev) => ({
                ...prev,
                userId: userId,
            }));
        }
    }, [userId]);

    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const { showSlowMsg, message } = useNetworkStatus(loading, 4000);
    const [leaveData, setLeaveData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(8);
    const [filteredData, setFilteredData] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const updated = { ...prev, [name]: value };
            if (name === "startDate" && !prev.endDate) {
                updated.endDate = value;
            }
            return updated;
        });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            setFilePreview(URL.createObjectURL(selectedFile));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (formData.endDate && new Date(formData.endDate) < new Date(formData.startDate)) {
                throw new Error("End date cannot be before start date");
            }

            const resolvedEndDate = formData.endDate || formData.startDate;
            const formDataToSend = new FormData();
            formDataToSend.append("start_date", formData.startDate);
            formDataToSend.append("end_date", resolvedEndDate);
            formDataToSend.append("leave_type", formData.leaveType);
            formDataToSend.append("user_remark", formData.remarks);
            formDataToSend.append("user_id", formData.userId);
            formDataToSend.append("leave_date", formData.startDate);

            if (file) {
                formDataToSend.append("leave_img", file);
            }

            const response = await fetch("https://api.mirai.healthcare/api/attendance/leaves", {
                method: "POST",
                headers: {
                    "x-auth": localStorage.getItem("token"),
                },
                body: formDataToSend,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to apply leave");
            }

            Swal.fire("Success", "Leave applied successfully!", "success");
            fetchLeaveData();
            resetForm();
        } catch (error) {
            console.error("Error applying leave:", error);
            Swal.fire("Error", "Failed to apply leave", "error");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            startDate: "",
            endDate: "",
            leaveType: "",
            remarks: "",
            userId: userId,
        });
        setFile(null);
        setFilePreview(null);
        setShowModal(false);
    };

    const fetchLeaveData = async () => {
        if (!userId) return;
        const cacheKey = `apply_leave_data_${userId}`;
        const cached = getCachedData(cacheKey);
        if (cached) {
            setLeaveData(cached);
            setFilteredData(cached);
        }
        try {
            const response = await fetch(`https://api.mirai.healthcare/api/attendance/leaves/${userId}`, {
                headers: {
                    "x-auth": localStorage.getItem("token"),
                },
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to fetch leave data");
            }

            const sortedData = (data.leaveData || []).sort((a, b) => new Date(b.leave_date) - new Date(a.leave_date));
            setLeaveData(sortedData);
            setFilteredData(sortedData);
            setCachedData(cacheKey, sortedData);
        } catch (error) {
            console.error("Error fetching leave data:", error);
        }
    };

    useEffect(() => {
        fetchLeaveData();
    }, [userId]);

    // Filter data based on search and status
    useEffect(() => {
        let filtered = leaveData;

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(item =>
                item.user_remark?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                new Date(item.leave_date).toLocaleDateString().includes(searchTerm)
            );
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(item => item.leave_status === statusFilter);
        }

        setFilteredData(filtered);
        setCurrentPage(1);
    }, [searchTerm, statusFilter, leaveData]);

    const today = new Date().toISOString().split("T")[0];

    // Calculate stats
    const totalLeaves = leaveData.length;
    const pendingLeaves = leaveData.filter(l => l.leave_status === 'pending').length;
    const approvedLeaves = leaveData.filter(
        (l) => l.leave_status === "approved" || l.leave_status === "accepted"
    ).length;
    const cancelledLeaves = leaveData.filter(l => l.leave_status === 'cancelled').length;

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const getStatusBadge = (status) => {
        const statusConfig = {
            approved: { variant: "success", icon: FaCheckCircle, label: "Approved" },
            accepted: { variant: "success", icon: FaCheckCircle, label: "Approved" },
            pending: { variant: "warning", icon: FaClock, label: "Pending" },
            rejected: { variant: "danger", icon: FaExclamationTriangle, label: "Rejected" },
            cancelled: { variant: "secondary", icon: FaExclamationTriangle, label: "Cancelled" },
            reapply: { variant: "info", icon: FaSync, label: "Re-Apply" },
            reapplied: { variant: "primary", icon: FaSync, label: "Re-Applied" },
            approvedwithcondition: {
                variant: "warning",
                icon: FaExclamationTriangle,
                label: "Approved with Condition",
            },
        };

        const config = statusConfig[status] || { variant: 'secondary', icon: FaExclamationTriangle, label: status };
        const IconComponent = config.icon;

        return (
            <Badge bg={config.variant} className="d-flex align-items-center gap-1 px-2 py-1">
                <IconComponent size={12} />
                {config.label}
            </Badge>
        );
    };

    const formatDate = (dateString) => {
        return dateString ? moment(dateString).format('DD MMM YYYY') : 'N/A';
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <Header />

                {/* Stats Row - Like the image */}
                <div className="row mb-4">
                    <div className="col-12">
                        <h4 className="mb-3 text-center">Apply Leaves</h4>
                        <div className="row g-3 justify-content-center">
                            <div className="col-md-3 col-sm-6">
                                <Card className="text-center border-0 shadow-sm h-100">
                                    <Card.Body>
                                        <Card.Title className="fs-1 text-primary mb-1">{totalLeaves}</Card.Title>
                                        <Card.Text className="text-muted mb-0">Total</Card.Text>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-3 col-sm-6">
                                <Card className="text-center border-0 shadow-sm h-100">
                                    <Card.Body>
                                        <Card.Title className="fs-1 text-warning mb-1">{pendingLeaves}</Card.Title>
                                        <Card.Text className="text-muted mb-0">Pending</Card.Text>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-3 col-sm-6">
                                <Card className="text-center border-0 shadow-sm h-100">
                                    <Card.Body>
                                        <Card.Title className="fs-1 text-success mb-1">{approvedLeaves}</Card.Title>
                                        <Card.Text className="text-muted mb-0">Approved</Card.Text>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-3 col-sm-6">
                                <Card className="text-center border-0 shadow-sm h-100">
                                    <Card.Body>
                                        <Card.Title className="fs-1 text-secondary mb-1">{cancelledLeaves}</Card.Title>
                                        <Card.Text className="text-muted mb-0">Cancelled</Card.Text>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Actions - Inspired by FeeHistory */}
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Body>
                        <Row className="align-items-center g-3">
                            <Col md={4}>
                                <InputGroup>
                                    <InputGroup.Text className="bg-light border-end-0">
                                        <FaSearch className="text-muted" />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by date or remarks..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="border-start-0"
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={3}>
                                <InputGroup>
                                    <InputGroup.Text className="bg-light border-end-0">
                                        <FaFilter className="text-muted" />
                                    </InputGroup.Text>
                                    <Form.Select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="pending">Pending</option>
                                        <option value="accepted">Approved</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="cancelled">Cancelled</option>
                                        <option value="reapply">Re-Apply</option>
                                        <option value="reapplied">Re-Applied</option>
                                        <option value="approvedwithcondition">Approved with Condition</option>
                                    </Form.Select>
                                </InputGroup>
                            </Col>
                            <Col md={2}>
                                <Button variant="primary" onClick={() => setShowModal(true)} className="w-100">
                                    <FaPlus className="me-2" />
                                    Add Leave
                                </Button>
                            </Col>
                            <Col md={3}>
                                <div className="d-flex gap-2">
                                    <Button variant="outline-secondary" className="flex-fill" onClick={fetchLeaveData}>
                                        <FaSync className="me-2" />
                                        Refresh
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* No Data Screen - Like the image */}
                {filteredData.length === 0 && (
                    <div className="row d-flex justify-content-center align-items-center min-vh-50">
                        <Col md={6} className="text-center">
                            <div className="mb-4">
                                <ImageWithBasePath
                                    src="assets/img/nodata.gif" // Or use a folder/document icon as in image
                                    className="img-fluid"
                                    alt="No Data"
                                    style={{ width: '150px', height: '150px' }}
                                />
                            </div>
                            <h5 className="text-muted mb-3">DATA NOT FOUND!</h5>
                            <p className="text-muted mb-4">Kindly check again for latest updates</p>
                            <Button variant="primary" onClick={() => setShowModal(true)}>
                                <FaPlus className="me-2" />
                                Add
                            </Button>
                        </Col>
                    </div>
                )}

                {/* Leave History List - Inspired by FeeHistory cards */}
                {filteredData.length > 0 && (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Leave History</h5>
                            <span className="text-muted small">
                                Showing {currentItems.length} of {filteredData.length} leaves
                            </span>
                        </div>

                        <div className="d-flex flex-column gap-3">
                            {currentItems.map((leave, index) => {
                                const startIndex = (currentPage - 1) * itemsPerPage;
                                return (
                                    <Card className="border-0 shadow-sm" key={leave.id}>
                                        <Card.Body>
                                            <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-3 flex-grow-1">
                                                    <div className="flex-shrink-0">
                                                        {leave.leave_img ? (
                                                            <img
                                                                src={leave.leave_img}
                                                                alt="Leave Document"
                                                                className="rounded"
                                                                style={{
                                                                    width: "60px",
                                                                    height: "60px",
                                                                    objectFit: "cover",
                                                                    cursor: "pointer"
                                                                }}
                                                                onClick={() => {
                                                                    setSelectedImage(leave.leave_img);
                                                                    setShowImageModal(true);
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ width: "60px", height: "60px" }}>
                                                                <FaFileAlt size={24} className="text-muted" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-1 d-flex align-items-center gap-2">
                                                            <Badge bg="light" text="primary">#{startIndex + index + 1}</Badge>
                                                            Leave Request
                                                            {getStatusBadge(leave.leave_status)}
                                                        </h6>
                                                        <div className="text-muted small d-flex flex-wrap gap-3">
                                                            <span>
                                                                <FaCalendarAlt className="me-1" />
                                                                {formatDate(leave.leave_date)}
                                                            </span>
                                                            <span>
                                                                <FaFileAlt className="me-1" />
                                                                {leave.leave_type || 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => {
                                                                setSelectedImage(leave.leave_img);
                                                                setShowImageModal(true);
                                                            }}
                                                        >
                                                            <FaEye className="me-1" />
                                                            View
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Remarks */}
                                            <div className="mt-3 pt-3 border-top">
                                                <small className="text-muted">Remarks:</small>
                                                <p className="mb-0 mt-1">{leave.user_remark || 'No remarks'}</p>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Pagination - Like FeeHistory */}
                        {totalPages > 1 && (
                            <Row className="mt-4">
                                <Col>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted">
                                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
                                        </div>
                                        <div className="pagination-container"> {/* Use Bootstrap Pagination */}
                                            {/* Implement pagination similar to FeeHistory */}
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </Button>
                                            <span className="mx-2">{currentPage} / {totalPages}</span>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </>
                )}

                {/* Apply Leave Modal */}
                <Modal
                    show={showModal}
                    onHide={() => {
                        setShowModal(false);
                        setFile(null);
                        setFilePreview(null);
                    }}
                    centered
                    size="lg"
                >
                    <form onSubmit={handleSubmit}>
                        <Modal.Header closeButton>
                            <Modal.Title>Apply Leave</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
          {showSlowMsg && (
            <div className="alert alert-warning text-center mx-3 my-2 py-2 fs-13">
              ⚠️ {message}
            </div>
          )}
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label">Start Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="startDate"
                                            value={formData.startDate}
                                            onChange={handleChange}
                                            min={today}
                                            required
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label">End Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="endDate"
                                            value={formData.endDate}
                                            onChange={handleChange}
                                            min={formData.startDate || today}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <div className="mb-3">
                                <label className="form-label">Leave Type</label>
                                <select
                                    className="form-control"
                                    name="leaveType"
                                    value={formData.leaveType}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Leave Type</option>
                                    <option value="fullday">Full Day</option>
                                    <option value="halfday">Half Day</option>
                                    <option value="medical">Medical Leave</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Remarks</label>
                                <textarea
                                    className="form-control"
                                    name="remarks"
                                    value={formData.remarks}
                                    onChange={handleChange}
                                    rows={3}
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Upload File</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={handleFileChange}
                                    required
                                />
                            </div>
                            {filePreview && (
                                <div className="mb-3 text-center">
                                    <label className="form-label">File Preview</label>
                                    <div
                                        style={{
                                            height: "300px",
                                            width: "100%",
                                            overflow: "hidden",
                                            margin: "0 auto",
                                            border: "1px solid #ddd",
                                            borderRadius: "8px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            background: "#f9f9f9",
                                        }}
                                    >
                                        <img
                                            src={filePreview}
                                            alt="Preview"
                                            style={{
                                                height: "300px",
                                                width: "auto",
                                                objectFit: "contain",
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowModal(false);
                                    setFile(null);
                                    setFilePreview(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
                            </Button>
                        </Modal.Footer>
                    </form>
                </Modal>

                {/* Image Modal */}
                <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>View Document</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedImage && <img src={selectedImage} alt="Document" style={{ width: "100%", height: "auto" }} />}
                    </Modal.Body>
                </Modal>
            </div>
        </div>
    );
}

export default ApplyLeave;
